import api from "./api"
import { AxiosError } from "axios"
import {
  SageDeleteResponse,
  SageEmployee,
  SageEmployeesResponse,
  SageEmployeeWrite,
} from "../Interfaces"
import { showErroPage } from "../utils/helper"
import { throwSageApiError } from "../utils/sageApiError"

class SageEmployeeService {
  private baseUrl = "/sage-100/employees"

  private companyEmployeesUrl(companyId: number) {
    return `/sage-100/companies/${companyId}/employees`
  }

  public getAll = async (options?: { managementCompanyId?: number }): Promise<SageEmployeesResponse> => {
    try {
      const params: { managementCompanyId?: number } = {}
      if (options?.managementCompanyId) {
        params.managementCompanyId = options.managementCompanyId
      }
      const response = await api.get(this.baseUrl, { params })
      const payload = response.data
      const data: SageEmployee[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.employees)
            ? payload.employees
            : []
      return {
        success: payload?.success !== false,
        sample: Boolean(payload?.sample),
        data,
        totalCount: payload?.totalCount ?? data.length,
        error: payload?.error,
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data)
        throw Error(error.response?.statusText)
      }
      console.error(error)
      showErroPage()
      return { success: false, data: [], totalCount: 0 }
    }
  }

  public create = async (companyId: number, body: SageEmployeeWrite): Promise<SageEmployee> => {
    if (!companyId) throw new Error("No management company id provided")
    try {
      const response = await api.post(this.companyEmployeesUrl(companyId), body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not add the employee.")
    }
  }

  public update = async (
    companyId: number,
    employeeId: number,
    body: Partial<SageEmployeeWrite>,
  ): Promise<SageEmployee> => {
    if (!companyId) throw new Error("No management company id provided")
    if (!employeeId) throw new Error("No employee id provided")
    try {
      const response = await api.patch(`${this.companyEmployeesUrl(companyId)}/${employeeId}`, body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not update the employee.")
    }
  }

  public delete = async (companyId: number, employeeId: number): Promise<SageDeleteResponse> => {
    if (!companyId) throw new Error("No management company id provided")
    if (!employeeId) throw new Error("No employee id provided")
    try {
      const response = await api.delete(`${this.companyEmployeesUrl(companyId)}/${employeeId}`)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not delete the employee.")
    }
  }
}

export const sageEmployeeService = new SageEmployeeService()
